import { View, XStack, YStack, useTheme } from "tamagui";

import Apple from "@/assets/icons/Apple";
import BloodPressureMonitor from "@/assets/icons/BloodPressureMonitor";
import Brain from "@/assets/icons/Brain";
import Health from "@/assets/icons/Health";
import Pathology from "@/assets/icons/Pathology";
import WeightScale from "@/assets/icons/WeightScale";
import { Button } from "@/components/Button";
import { Text } from "@/components/Typography";
import { usePropagatedRouter } from "@/hooks/usePropagatedRouter";

type CoverageCategory = {
  Icon: React.ComponentType<{ size?: number; color?: string }>;
  title: string;
};

const CATEGORIES: CoverageCategory[] = [
  { Icon: Health, title: "Acute & Primary care" },
  { Icon: Pathology, title: "Screenings & labs" },
  { Icon: Brain, title: "Mental health" },
  { Icon: WeightScale, title: "Weight management & GLP-1s" },
  { Icon: Apple, title: "Wellness" },
  { Icon: BloodPressureMonitor, title: "Chronic conditions" },
];

function CategoryCard({ category }: { category: CoverageCategory }) {
  const { Icon, title } = category;
  const theme = useTheme();

  return (
    <XStack
      width="100%"
      $lg={{ width: "calc(50% - 12px)" }}
      borderWidth="$strokeWidth"
      borderColor="$strokeSubdued"
      borderRadius="$200"
      paddingHorizontal="$300"
      paddingVertical="$250"
      alignItems="center"
      gap="$200"
    >
      <View
        backgroundColor="$strokeSubdued"
        borderRadius="$3000"
        padding="$150"
        flexShrink={0}
      >
        <Icon size={16} color={theme.iconBase.get()} />
      </View>
      <Text size="$sm">{title}</Text>
    </XStack>
  );
}

export default function HealthCoverage({
  onPress,
  disabled,
}: {
  onPress?: () => void;
  disabled?: boolean;
}) {
  const router = usePropagatedRouter();
  const handlePress = onPress ?? (() => router.push("/sign-in"));

  return (
    <YStack
      maxWidth="$landingPageConstraint"
      alignSelf="center"
      backgroundColor="$backgroundGround"
      paddingHorizontal="$400"
      paddingVertical="$800"
      gap="$600"
      alignItems="center"
      width="100%"
    >
      <YStack alignItems="center" gap="$100">
        <Text size="$xxl" textAlign="center">
          Everything for your health, in one place
        </Text>
        <Text size="$sm" textAlign="center" color="$textSecondary">
          From everyday symptoms to ongoing care - Curai is here for you.
        </Text>
      </YStack>

      <XStack width="100%" flexWrap="wrap" gap="$300">
        {CATEGORIES.map((category) => (
          <CategoryCard key={category.title} category={category} />
        ))}
      </XStack>

      <Button mode="secondary" onPress={handlePress} disabled={disabled}>
        Get started now →
      </Button>
    </YStack>
  );
}
